import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { ResponseDeclaracaoRecibimentoDto } from "../../../shared/dtos/Declaracao-anual-voluntaria.dto";
import { formatCurrency } from "../../../utils/formatCurrency/formatCurrency";

// Estilos para o PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 12,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
    position: "relative",
  },
  logoContainer: {
    position: "absolute",
    left: 40,
    top: 0,
    width: 45,
    height: 45,
  },
  logo: {
    width: 45,
    height: 45,
  },
  headerText: {
    flex: 1,
    alignItems: "center",
  },
  instituicao: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 2,
  },
  detalhes: {
    fontSize: 10,
    marginBottom: 1,
  },
  titulo: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginTop: 10,
    marginBottom: 20,
  },
  doador: {
    marginBottom: 5,
    fontWeight: "bold",
  },
  tabela: {
    flexDirection: "column",
    marginBottom: 20,
    borderTop: "1px solid black",
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
  },
  linha: {
    flexDirection: "row",
    borderBottom: "1px solid black",
  },
  coluna1: {
    width: "30%",
    padding: 5,
    borderRight: "1px solid black",
  },
  coluna2: {
    flex: 1,
    padding: 5,
  },
  subtitulo: {
    fontSize: 11,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 5,
  },
  resumoContainer: {
    marginBottom: 15,
  },
  resumoLinha: {
    flexDirection: "row",
    fontSize: 9,
    marginBottom: 2,
  },
  resumoTipo: {
    width: "40%",
  },
  resumoQtd: {
    width: "20%",
  },
  resumoValor: {
    width: "40%",
  },
  tabelaDoacoes: {
    flexDirection: "column",
    marginBottom: 20,
    borderTop: "1px solid black",
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
    fontSize: 9,
  },
  linhaDoacoes: {
    flexDirection: "row",
    borderBottom: "1px solid black",
  },
  colunaDoacoes: {
    padding: 4,
    borderRight: "1px solid black",
  },
  colunaDoacoesHeader: {
    padding: 4,
    borderRight: "1px solid black",
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
  },
  colunaData: {
    width: "25%",
  },
  colunaTipo: {
    width: "35%",
  },
  colunaValor: {
    width: "25%",
  },
  colunaId: {
    width: "15%",
    borderRight: "0px solid black",
  },
  declaracao: {
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 10,
    lineHeight: 1.5,
  },
  observacao: {
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 20,
    lineHeight: 1.5,
  },
  assinatura: {
    fontSize: 10,
    marginTop: 30,
    textAlign: "center",
  },
  assinaturaLinha: {
    fontSize: 10,
    marginTop: 2,
    textAlign: "center",
  },
  dataAssinatura: {
    fontSize: 10,
    marginTop: 30,
    textAlign: "left",
  },
  rodape: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    fontSize: 8,
    textAlign: "left",
    color: "#555",
  },
  rodapeInfo: {
    position: "absolute",
    bottom: 10,
    left: 30,
    right: 30,
    fontSize: 8,
    color: "#555",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  campoVazio: {
    color: "#666666",
    fontStyle: "italic",
  },
});

const DeclaracaoPDF: React.FC<{ data: ResponseDeclaracaoRecibimentoDto }> = ({
  data,
}) => {
  const dataAtual = new Date().toLocaleDateString("pt-BR");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <Image
              src={require("../../../assets/logo-imagem.png")}
              style={styles.logo}
            />
          </View>
          <View style={styles.headerText}>
            <Text style={styles.instituicao}>
              Associação Beneficente Nossa Senhora da Conceição
            </Text>
            <Text style={styles.detalhes}>
              Santa Casa de Misericórdia e Maternidade Nossa Senhora da
              Conceição
            </Text>
            <Text style={styles.detalhes}>
              CNPJ 47.531.835/0001-31 Inscrição Estadual: isenta
            </Text>
            <Text style={styles.detalhes}>
              Avenida Padre Rodolfo, 320 - Alto do Cruzeiro - 12530-000 -
              Cunha-SP
            </Text>
          </View>
        </View>

        {/* Título */}
        <Text style={styles.titulo}>
          DECLARAÇÃO DE RECEBIMENTO ANUAL DE DOAÇÃO VOLUNTÁRIA
        </Text>

        {/* Doador */}
        <Text style={styles.doador}>DOADOR:</Text>

        {/* Tabela com dados do doador */}
        <View style={styles.tabela}>
          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Nome completo:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>{data.contribuinte.nome}</Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>CPF:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>{data.contribuinte.cpfCin}</Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>RG:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text
                style={!data.contribuinte.rg ? styles.campoVazio : undefined}
              >
                {data.contribuinte.rg ? data.contribuinte.rg : "Não informado"}
              </Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Endereço:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>{data.contribuinte.endereco}</Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Complemento:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text
                style={
                  !data.contribuinte.complementoEndereco
                    ? styles.campoVazio
                    : undefined
                }
              >
                {data.contribuinte.complementoEndereco
                  ? data.contribuinte.complementoEndereco
                  : "Não informado"}
              </Text>
            </View>
          </View>
          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Bairro:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>{data.contribuinte.bairro}</Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Cidade/Estado:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>
                {data.contribuinte.cidade}/{data.contribuinte.uf}
              </Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Período:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>
                01/01/{data.anoInicial} a 31/12/{data.anoInicial}
              </Text>
            </View>
          </View>

          <View style={styles.linha}>
            <View style={styles.coluna1}>
              <Text>Valor total recebido:</Text>
            </View>
            <View style={styles.coluna2}>
              <Text>{formatCurrency(data.valorTotal.toString())}</Text>
            </View>
          </View>
        </View>

        {/* Sempre exibir o resumo por tipo de doação */}
        <Text style={styles.subtitulo}>Resumo por tipo de doação:</Text>
        <View style={styles.resumoContainer}>
          <View style={styles.resumoLinha}>
            <Text style={styles.resumoTipo}>Tipo</Text>
            <Text style={styles.resumoQtd}>Quantidade</Text>
            <Text style={styles.resumoValor}>Valor</Text>
          </View>

          <View style={styles.resumoLinha} key={"ResumoMensaliade"}>
            <Text style={styles.resumoTipo}>Mensalidade</Text>
            <Text style={styles.resumoQtd}>
              {data.totalMensalidade.toString()}
            </Text>
            <Text style={styles.resumoValor}>
              {formatCurrency(data.valorTotalMensalidade.toString())}
            </Text>
          </View>

          <View style={styles.resumoLinha} key={"ResumoEsporadica"}>
            <Text style={styles.resumoTipo}>Esporádica</Text>
            <Text style={styles.resumoQtd}>
              {data.totalEsporadica.toString()}
            </Text>
            <Text style={styles.resumoValor}>
              {formatCurrency(data.valorTotalEsporatica.toString())}
            </Text>
          </View>
        </View>

        {/* Declaração */}
        <Text style={styles.declaracao}>
          Declaro para os devidos fins e conforme artigo 11, parágrafo único do
          Estatuto da Associação Beneficente Nossa Senhora da Conceição, que
          recebemos no período expresso do doador acima qualificado, a
          importância total especificada a título de doação voluntária a esta
          entidade.
        </Text>

        {/* Observação */}
        <Text style={styles.observacao}>
          Vale ressaltar que esta doação não se configura como despesas médicas,
          de hospitais, clínicas, laboratórios e/ou plano de saúde conforme
          estabelece a Receita Federal (código 21). Poderá ser lançado em
          Doações Efetuadas - código 99 - Outras.
        </Text>

        {/* Local e data */}
        <Text style={styles.dataAssinatura}>
          Cunha-SP, {dataAtual.split("/")[0]} de{" "}
          {new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(
            new Date()
          )}{" "}
          de {dataAtual.split("/")[2]}.
        </Text>

        {/* Assinatura */}
        <Text>{"\n\n\n\n\n"}</Text>
        <Text style={styles.assinatura}>
          _____________________________________________________
        </Text>
        <Text style={styles.assinaturaLinha}>Provedor</Text>

        {/* Rodapé */}
        <View style={styles.rodape}>
          <Text>
            Declaração emitida com autorização do doador e conforme Lei Geral de
            Proteção de Dados.
          </Text>
          <Text>1a via solicitante / 2a via Santa Casa</Text>
        </View>

        {/* Informações de emissão */}
        <View style={styles.rodapeInfo}>
          <Text>Data de emissão: {dataAtual}</Text>
          <Text>Desenvolvido por FATECOINS</Text>
        </View>
      </Page>
    </Document>
  );
};

export default DeclaracaoPDF;
